'use client'

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { QuoteList } from './components/QuoteList'
import { QuoteDetailPanel } from './components/QuoteDetailPanel'
import { QuoteDetailSheet } from './components/QuoteDetailSheet'
import { useQuotesInbox } from './hooks/useQuotesInbox'
import { toast } from '@/hooks/use-toast'
import { RequestsPipelinePanel, type RequestStatusFilter } from './components/RequestsPipelinePanel'

export default function QuotesInboxClient() {
  const router = useRouter()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [activeTab, setActiveTab] = useState<'received' | 'requests'>('received')
  const [pendingRequestCount, setPendingRequestCount] = useState(0)
  const [creditBalance, setCreditBalance] = useState<number | null>(null)
  const [compareIds, setCompareIds] = useState<number[]>([])
  const [requestStatusFilter, setRequestStatusFilter] = useState<RequestStatusFilter>('all')

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    ;(async () => {
      try {
        const [statsRes, creditRes] = await Promise.all([
          fetch('/api/client/estimate-request-groups/stats'),
          fetch('/api/my/client/billing/request-credits/balance'),
        ])

        if (statsRes.ok) {
          const data = await statsRes.json()
          setPendingRequestCount(data?.targetStats?.pending || 0)
        }

        if (creditRes.ok) {
          const data = await creditRes.json()
          setCreditBalance((data?.free_remaining || 0) + (data?.paid_balance || 0))
        }
      } catch (e) {
        console.error(e)
      }
    })()
  }, [])

  const handleEstimateSelect = useCallback(
    (estimate: any) => {
      if (estimate && isMobile) setSheetOpen(true)
    },
    [isMobile]
  )

  const {
    loading,
    estimates,
    projectGroups,
    projectChips,
    selectedEstimate,
    selectedEstimateId,
    selectedProjectId,
    setSelectedProjectId,
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    selectEstimate,
    acceptEstimate,
    rejectEstimate,
    isRead,
  } = useQuotesInbox({ onEstimateSelect: handleEstimateSelect })

  const unreadCount = useMemo(
    () => estimates.filter((estimate) => (estimate as any).is_read === false).length,
    [estimates]
  )

  const resetFilters = useCallback(() => {
    setStatusFilter('all')
    setSearchTerm('')
    setSortBy('updated_at')
    setSelectedProjectId(null)
  }, [setSearchTerm, setSelectedProjectId, setSortBy, setStatusFilter])

  const displayEstimates =
    projectGroups.length > 0 ? projectGroups.flatMap((group) => group.estimates) : estimates

  const compareCandidates = useMemo(
    () => displayEstimates.filter((estimate) => compareIds.includes(estimate.estimate_id)),
    [displayEstimates, compareIds]
  )

  const compareLimitReached = compareIds.length >= 3

  const handleToggleCompare = useCallback((estimateId: number, checked: boolean) => {
    setCompareIds((prev) => {
      if (checked) {
        if (prev.includes(estimateId) || prev.length >= 3) return prev
        return [...prev, estimateId]
      }
      return prev.filter((id) => id !== estimateId)
    })
  }, [])

  const handleCompare = useCallback(() => {
    if (compareCandidates.length < 2) {
      toast({ title: '비교할 견적을 2개 이상 선택해주세요.' })
      return
    }

    const uniqueProjects = new Set(compareCandidates.map((item) => item.counsel_id).filter(Boolean))
    if (uniqueProjects.size !== 1) {
      toast({
        variant: 'destructive',
        title: '같은 프로젝트의 견적만 비교할 수 있어요.',
      })
      return
    }

    const [projectId] = Array.from(uniqueProjects)
    router.push(`/client/projects/${projectId}/quotes`)
  }, [compareCandidates, router])

  const handleSendRequest = useCallback(() => {
    const query = selectedProjectId ? `?step=1&counselId=${selectedProjectId}` : '?step=1'
    router.push(`/client/requests/new${query}`)
  }, [router, selectedProjectId])

  const handleSendRequestForProject = useCallback(
    (counselId: number) => {
      router.push(`/client/requests/new?step=1&counselId=${counselId}`)
    },
    [router]
  )

  return (
    <div className="flex flex-col bg-white min-h-[calc(100vh-var(--header-height,0px))]">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="mx-auto max-w-7xl px-4 py-3 space-y-3">
          <div className="flex items-center gap-2 min-w-0 overflow-x-auto">
            <button
              type="button"
              className={`shrink-0 px-3 py-2 text-sm leading-tight rounded-lg font-medium whitespace-nowrap ${activeTab === 'received' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
              onClick={() => setActiveTab('received')}
            >
              받은 견적 <span className="ml-1 text-xs opacity-90">미확인 {unreadCount}</span>
            </button>
            <button
              type="button"
              className={`shrink-0 px-3 py-2 text-sm leading-tight rounded-lg font-medium whitespace-nowrap ${activeTab === 'requests' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
              onClick={() => setActiveTab('requests')}
            >
              견적 요청 <span className="ml-1 text-xs opacity-90">대기중 {pendingRequestCount}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3">
            <div className="md:col-span-2">
              <Select value={selectedProjectId ? String(selectedProjectId) : 'all'} onValueChange={(value) => setSelectedProjectId(value === 'all' ? null : Number(value))}>
                <SelectTrigger className="h-9 bg-white"><SelectValue placeholder="프로젝트 선택" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">전체 프로젝트</SelectItem>
                  {projectChips.map((project) => (
                    <SelectItem key={project.counsel_id} value={String(project.counsel_id)}>{project.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              {activeTab === 'received' ? (
                <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
                  <SelectTrigger className="h-9 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">전체</SelectItem>
                    <SelectItem value="decision_needed">검토중</SelectItem>
                    <SelectItem value="negotiating">협의중</SelectItem>
                    <SelectItem value="accept">수락</SelectItem>
                    <SelectItem value="rejected">거절</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Select value={requestStatusFilter} onValueChange={(v: RequestStatusFilter) => setRequestStatusFilter(v)}>
                  <SelectTrigger className="h-9 bg-white"><SelectValue placeholder="상태" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">전체</SelectItem>
                    <SelectItem value="pending">대기중</SelectItem>
                    <SelectItem value="read">열람</SelectItem>
                    <SelectItem value="accepted">응답 받음</SelectItem>
                    <SelectItem value="declined">거절</SelectItem>
                    <SelectItem value="canceled">만료·취소</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="md:col-span-2">
              <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                <SelectTrigger className="h-9 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="updated_at">최근 업데이트순</SelectItem>
                  <SelectItem value="amount_asc">금액 낮은순</SelectItem>
                  <SelectItem value="submitted_at">응답 빠른순</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4">
              <Input
                className="h-9 bg-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={activeTab === 'requests' ? '프로젝트명, 대상(팀/개인) 검색' : '프로젝트명, 프리랜서, 팀명 검색'}
              />
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-2 min-w-0">
              <Button
                className="h-9 min-h-9 flex-1 md:flex-initial min-w-0 text-sm leading-tight px-3"
                onClick={handleSendRequest}
              >
                <span className="block truncate">{activeTab === 'requests' ? '새 요청 보내기 (1크레딧)' : '견적 요청 보내기 (1크레딧)'}</span>
              </Button>
              <span className="text-xs text-gray-600 whitespace-nowrap shrink-0">잔여 {creditBalance ?? '-'}크레딧</span>
            </div>
          </div>
        </div>
      </div>

      {activeTab === 'received' ? (
        <div className="flex-1 flex overflow-hidden min-h-0">
          <div className="hidden md:flex flex-1 min-h-0">
            <div className="w-[35%] border-r border-gray-200 bg-white overflow-hidden flex flex-col min-h-0">
              <QuoteList
                estimates={displayEstimates}
                projectGroups={projectGroups}
                selectedEstimateId={selectedEstimateId}
                loading={loading}
                isRead={isRead}
                onSelect={selectEstimate}
                onResetFilters={resetFilters}
                hasActiveFilters={Boolean(searchTerm || selectedProjectId || statusFilter !== 'all')}
                compareSelection={compareIds}
                compareLimitReached={compareLimitReached}
                onToggleCompare={handleToggleCompare}
                onSendRequestForProject={handleSendRequestForProject}
              />
            </div>

            <div className="w-[65%] bg-white overflow-hidden flex flex-col min-h-0">
              <QuoteDetailPanel
                estimate={selectedEstimate}
                viewAccess={null}
                freeQuota={null}
                loading={false}
                onAccept={acceptEstimate}
                onReject={rejectEstimate}
                onAccessGranted={() => {}}
                onSendRequestForProject={handleSendRequestForProject}
              />
            </div>
          </div>

          <div className="md:hidden flex-1 overflow-hidden min-h-0">
            <QuoteList
              estimates={displayEstimates}
              selectedEstimateId={selectedEstimateId}
              loading={loading}
              isRead={isRead}
              onSelect={selectEstimate}
              onResetFilters={resetFilters}
              hasActiveFilters={Boolean(searchTerm || selectedProjectId || statusFilter !== 'all')}
              compareSelection={compareIds}
              compareLimitReached={compareLimitReached}
              onToggleCompare={handleToggleCompare}
            />
          </div>

          <QuoteDetailSheet
            estimate={selectedEstimate}
            viewAccess={null}
            freeQuota={null}
            loading={false}
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            onAccept={acceptEstimate}
            onReject={rejectEstimate}
            onAccessGranted={() => {}}
            onSendRequestForProject={handleSendRequest}
          />
        </div>
      ) : (
        <div className="mx-auto w-full max-w-7xl">
          <RequestsPipelinePanel
            projectFilter={selectedProjectId}
            searchTerm={searchTerm}
            requestStatusFilter={requestStatusFilter}
            onRequestStatusFilterChange={setRequestStatusFilter}
            onSendRequest={handleSendRequest}
            onSendRequestForProject={handleSendRequestForProject}
            creditBalance={creditBalance}
          />
        </div>
      )}

      {activeTab === 'received' && compareIds.length > 0 && (
        <div className="fixed bottom-4 left-1/2 z-30 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
          <div className="flex items-center justify-between gap-3 min-w-0">
            <p className="text-sm text-gray-700 truncate">비교함 {compareIds.length}/3</p>
            <Button size="sm" className="whitespace-nowrap shrink-0" onClick={handleCompare}>비교하기</Button>
          </div>
        </div>
      )}
    </div>
  )
}
